// File: src/pages/HouseProductList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Form,
  Alert,
  Table,
} from "reactstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";
import { Trash2 } from "react-feather";

// === Your shared modules (as per your rules) ===
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import axiosConfig from "../import/axiosConfig";

import { _Get, _Delete } from "../import/ApiEndPoint/ApiCalling";
import {
  PurchaseProductList_Product,
  View_CustomerGroup,
  Product_Price_Bulk_Update,
  Delete_Product,
  view_create_order_history,
  // backend route to update profitColorCode on products
  Update_Product_Profit_Colors,
} from "../import/ApiEndPoint/Api";

// ---- tiny CSS injected ONCE (page scoped) ----
const injectOnce = () => {
  if (document.getElementById("price-list-inline-css")) return;
  const style = document.createElement("style");
  style.id = "price-list-inline-css";
  style.innerHTML = `
    .pl-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .pl-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .pl-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:900px; }
    .pl-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .pl-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center; }
    .pl-table tbody tr:hover{ background:#f9fbff; }
    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-delete{ background:#ec1809; }

    .pl-form-card{ border:1px solid #e5e7eb; border-radius:14px; background:#fff; margin-top:10px; box-shadow:0 6px 16px rgba(0,0,0,.06); }
    .pl-modal-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:6px; }

    .no-scroll-modal{ overflow:hidden; }
    .no-scroll-modal .modal-body{ overflow:hidden !important; }

    .listBox{ border:1px solid #e5e7eb; border-radius:10px; max-height:280px; overflow:auto; background:#fff; padding:8px; }
    .listItem{ padding:8px 10px; border-radius:8px; border:1px solid #e5e7eb; margin-bottom:6px; background:#fff; cursor:pointer; }

    /* super wide dialog */
    .modal-dialog.modal-xxl {
      max-width: 98vw;
      margin: 0 auto;
    }
    .no-scroll-modal {
      overflow: hidden !important;
    }
    .no-scroll-modal .modal-body {
      overflow: hidden !important;
      max-height: none !important;
    }
  `;
  document.head.appendChild(style);
};

// ---- helpers ----
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const asMoney = (v) =>
  v === null || v === undefined ? "" : Number(v).toFixed(2);
const landedCostOf = (r) =>
  Number(r?.Purchase_Rate) > Number(r?.landedCost)
    ? Number(r?.Purchase_Rate)
    : Number(r?.landedCost);

// compute row fields so Sale Rate always = LandedCost + Profit% (min 3%)
const withDerived = (r, maxDiscount) => {
  const base = { ...r };
  const landed = landedCostOf(base) || 0;
  const p = Math.max(Number(base?.ProfitPercentage || 0), 3); // enforce min 3%
  const sales = Number((landed * ((100 + p) / 100)).toFixed(2));
  const disc = Number(base?.maxDiscount ?? maxDiscount ?? 0);
  const gd = Number(((sales * disc) / 100).toFixed(2));
  const tax = Number(
    (((sales + gd) * Number(base?.GSTRate || 0)) / 100).toFixed(2),
  );
  const mrp = Number((sales + gd + tax).toFixed(2));
  base.ProfitPercentage = p;
  base.SalesRate = sales;
  base.Product_MRP = mrp;
  base.maxDiscount = disc;
  base.lossStatus = landed > sales;
  base.__previousSalesRate = Number(r?.SalesRate ?? sales); // for bulk-edit sales rate formula
  return base;
};

// ==== PROFITABILITY COLOR LOGIC ====
// Default colors as per your requirement
const defaultColorConfig = {
  green: "#75fa8e", // 15% and above
  orange: "#d9614c", // 10% - 14.99%
  blue: "#87c8fa", // 5% - 9.99%
  red: "#f78981", // 0% - 4.99%
};

// ⬇️ FIX 1: more robust parsing of ProfitPercentage (handles "15%" etc)
const getProfitColor = (profitPercentage, colorConfig) => {
  const raw = profitPercentage ?? "";
  const normalized = String(raw).replace(/[^\d.-]/g, "");
  const p = normalized === "" ? 0 : Number(normalized) || 0;

  const merged = {
    ...defaultColorConfig,
    ...(colorConfig || {}),
  };

  const { green, orange, blue, red } = merged;

  if (p >= 15) return green;
  if (p >= 10) return orange;
  if (p >= 5) return blue;
  return red;
};

export default function HouseProductList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
    BulkUpload: true,
  });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");

  const [maxDiscount, setMaxDiscount] = useState(0);

  const [isMaster, setIsMaster] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  // Column chooser state
  const [modal, setModal] = useState(false);
  const [bulkEdit, setBulkEdit] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  // Bulk edit state
  const [formValues, setFormValues] = useState([]);
  const [profitThreshold, setProfitThreshold] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Color config state
  const [colorConfig, setColorConfig] = useState(defaultColorConfig);
  const [savingColors, setSavingColors] = useState(false);

  // Selected visible columns (persist as "PriceList")
  const baseColumns = useMemo(
    () => [
      {
        key: "Product_Title",
        header: "PRODUCT NAME",
        get: (r) => r?.Product_Title,
        render: (r) => (
          <span
            style={{
              color: getProfitColor(r?.ProfitPercentage, colorConfig),
              fontWeight: 600,
            }}
          >
            {r?.Product_Title}
          </span>
        ),
      },
      { key: "HSN_Code", header: "HSN CODE", get: (r) => r?.HSN_Code },
      {
        key: "Purchase_Rate",
        header: "PURCHASE RATE",
        get: (r) => {
          const cost =
            r?.landedCost > r?.Purchase_Rate ? r?.landedCost : r?.Purchase_Rate;
          return cost;
        },
      },
      {
        key: "ProfitPercentage",
        header: "PROFIT %",
        get: (r) => (Number(r?.ProfitPercentage) > 3 ? r?.ProfitPercentage : 3),
      },
      {
        key: "SalesRate",
        header: "SALES RATE",
        get: (r) =>
          r?.SalesRate != null ? Number(r?.SalesRate).toFixed(2) : "",
        render: (r) => (
          <span
            style={{
              color: r?.lossStatus ? "#b91c1c" : "#065f46",
              fontWeight: 600,
            }}
          >
            {r?.SalesRate != null ? Number(r?.SalesRate).toFixed(2) : ""}
          </span>
        ),
      },
      { key: "maxDiscount", header: "GRADE DISCOUNT", get: () => maxDiscount },
      { key: "GSTRate", header: "TAX", get: (r) => r?.GSTRate },
      {
        key: "Product_MRP",
        header: "PRODUCT MRP",
        get: (r) => (r?.Product_MRP != null ? asMoney(r?.Product_MRP) : ""),
      },
    ],
    [maxDiscount, colorConfig],
  );

  const [selectedKeys, setSelectedKeys] = useState([]);
  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  useEffect(() => {
    injectOnce();
  }, []);

  // Load saved color config (if any) from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("PriceListColorConfig_v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setColorConfig((prev) => ({ ...prev, ...parsed }));
        }
      }
    } catch {
      // ignore parse errors; keep defaults
    }
  }, []);

  // update color config + persist
  const updateColorConfig = (key, value) => {
    const val = value || defaultColorConfig[key];
    setColorConfig((prev) => {
      const next = { ...prev, [key]: val };
      try {
        localStorage.setItem("PriceListColorConfig_v1", JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  // Restore visible columns from localStorage
  useEffect(() => {
    const raw = localStorage.getItem("PriceList");
    let nextKeys = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        if (typeof parsed[0] === "string") {
          nextKeys = parsed.filter((k) => colMap.has(k));
        } else if (parsed[0]?.field || parsed[0]?.key) {
          const keys = parsed.map((c) => c.field || c.key).filter(Boolean);
          nextKeys = keys.filter((k) => colMap.has(k));
        }
      }
    } catch {}
    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
    setSelectedKeys(nextKeys);
  }, [baseColumns, colMap]);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap],
  );

  // Fetch data
  const reload = async () => {
    const userinfo = JSON.parse(localStorage.getItem("userData") || "{}");
    const uid = userinfo?._id || userinfo?.id || "";
    const db = userinfo?.database || userinfo?.db || "";
    if (!uid || !db) throw new Error("Missing _id or database in userData");

    const inside = CheckPermission("Price List");
    if (inside && typeof inside === "object")
      setPermissions((p) => ({ ...p, ...inside }));

    setIsMaster(userinfo?.rolename?.roleName === "MASTER");

    let max = 0;
    try {
      const resGroup = await _Get(View_CustomerGroup, db);
      const groups = resGroup?.CustomerGroup || [];
      const active = groups.filter((g) => g?.status === "Active");
      max = active.reduce(
        (m, g) => (Number(g?.discount) > m ? Number(g?.discount) : m),
        0,
      );
      setMaxDiscount(max);
    } catch {
      setMaxDiscount(0);
    }

    let invoiceOverrides = new Map();
    try {
      const invRes = await _Get(view_create_order_history, uid, db);
      const history = Array.isArray(invRes?.orderHistory)
        ? invRes.orderHistory
        : [];

      history
        .filter((ord) => {
          const s = (ord?.status || "").toLowerCase();
          return s !== "deactive" && s === "completed";
        })
        .forEach((ord) => {
          const items = Array.isArray(ord?.orderItems) ? ord.orderItems : [];
          items.forEach((it) => {
            const pid =
              (it?.productId &&
                (it.productId._id || it.productId.id || it.productId)) ||
              it?.ProductId ||
              it?.product_id ||
              null;

            const basicPrice = Number(it?.price);
            if (!pid || !Number.isFinite(basicPrice) || basicPrice <= 0) return;

            const key = String(pid);
            const prev = invoiceOverrides.get(key) ?? 0;
            if (basicPrice > prev) {
              invoiceOverrides.set(key, basicPrice);
            }
          });
        });
    } catch (err) {
      console.error(
        "[PriceList] unable to read completed invoices for price overrides:",
        err,
      );
      invoiceOverrides = new Map();
    }

    try {
      const resProd = await _Get(PurchaseProductList_Product, db);
      const list = Array.isArray(resProd?.Product)
        ? resProd.Product.slice().reverse()
        : [];

      list.forEach((ele) => {
        const landed = landedCostOf(ele) || 0;

        let p = Number(ele?.ProfitPercentage);
        if (!Number.isFinite(p) || p <= 0) p = 3;

        let salesRate = Number(ele?.SalesRate);
        if (!Number.isFinite(salesRate)) {
          const mrp = Number(ele?.Product_MRP || 0);
          const gstFactor = (100 + Number(ele?.GSTRate || 0)) / 100;
          const disFactor = (100 + Number(max || 0)) / 100;
          const baseFromMrp =
            gstFactor && disFactor ? mrp / (gstFactor * disFactor) : 0;
          salesRate = Number.isFinite(baseFromMrp)
            ? Number(baseFromMrp.toFixed(2))
            : 0;
        }

        const override = invoiceOverrides.get(String(ele?._id || ele?.id));
        if (override && Number.isFinite(override) && override > salesRate) {
          salesRate = override;

          if (landed > 0) {
            const rawPct = ((salesRate - landed) / landed) * 100;
            p = Math.max(3, rawPct);
          }
        }

        ele.ProfitPercentage = p.toFixed(2);
        ele.SalesRate = Number(salesRate.toFixed(2));

        const discountPer = Number(max || 0);
        const gd = Number(((ele.SalesRate * discountPer) / 100).toFixed(2));
        const tax = Number(
          (((ele.SalesRate + gd) * Number(ele?.GSTRate || 0)) / 100).toFixed(2),
        );
        ele.Product_MRP = Number((ele.SalesRate + gd + tax).toFixed(2));

        ele.maxDiscount = max;
        ele.lossStatus = landed > ele.SalesRate;
      });

      setRows(list);
      setAllRows(list);
    } catch (e) {
      throw e;
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        await reload();
      } catch (e) {
        console.error("[PriceList] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load product price list. Check console for details.",
        );
        setRows([]);
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const cols = visibleCols;
    return rows.filter((r) =>
      cols.some((c) => toStr(c.get(r)).toLowerCase().includes(term)),
    );
  }, [rows, search, visibleCols]);

  const openModal = (bulk = false) => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setBulkEdit(bulk);
    if (bulk) {
      setFormValues(
        rows.map((r) => {
          const x = withDerived(r, maxDiscount);
          x.__previousSalesRate = Number(r?.SalesRate ?? x.SalesRate ?? 0);
          return x;
        }),
      );
    }
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("PriceList", JSON.stringify(selectedKeys));
    closeModal();
  };

  const handleExportPDF = async () => {
    const cols = visibleCols;
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r) =>
      cols
        .map((c) => {
          const v = toStr(c.get(r)).replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(","),
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "PriceList");
  };

  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols;
    const data = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  const handleDelete = (id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        _Delete(Delete_Product, id)
          .then(() => {
            setRows((prev) => prev.filter((x) => x._id !== id));
            setAllRows((prev) => prev.filter((x) => x._id !== id));
          })
          .catch(() => {});
      }
    });
  };

  const handleSaveColorConfig = async () => {
    setSavingColors(true);
    try {
      const userinfo = JSON.parse(localStorage.getItem("userData") || "{}");
      const db = userinfo?.database || userinfo?.db || "";
      if (!db) throw new Error("Missing database in userData");

      const productColors = rows
        .map((r) => {
          const color = getProfitColor(r?.ProfitPercentage, colorConfig);
          if (!r?._id || !color) return null;

          const profitPercentage = Number(
            String(r?.ProfitPercentage ?? 0).replace(/[^\d.-]/g, ""),
          );

          return {
            id: r._id,
            color,
            ProfitPercentage: Number.isFinite(profitPercentage)
              ? Number(profitPercentage.toFixed(2))
              : 0,
          };
        })
        .filter(Boolean);

      if (!productColors.length) {
        throw new Error("No products to update");
      }

      await axiosConfig.post(`${Update_Product_Profit_Colors}/${db}`, {
        productColors,
      });

      swal(
        "Success",
        "Profit color codes and profit percentages updated on products.",
        "success",
      );

      await reload();
    } catch (err) {
      console.error("[PriceList] color config save error:", err);
      swal(
        "Error",
        err?.response?.data?.message ||
          err?.message ||
          "Unable to update product color codes. Try again later.",
        "error",
      );
    } finally {
      setSavingColors(false);
    }
  };

  // ---- Bulk edit logic ----
  const handleInlineChange = (idx, name, value) => {
    setFormValues((prev) => {
      const next = prev.slice();
      const row = { ...next[idx] };

      const previousSaleRate = Number(
        row?.__previousSalesRate ?? row?.SalesRate ?? 0,
      );

      row[name] = value;

      const purchaseRate = Number(row?.Purchase_Rate || 0);
      const landedCost =
        Number(row?.Purchase_Rate) > Number(row?.landedCost)
          ? Number(row?.Purchase_Rate)
          : Number(row?.landedCost);

      if (name === "SalesRate") {
        const currentSalesRate = Number(value || 0);
        row.SalesRate = Number(currentSalesRate.toFixed(2));

        const divisor = purchaseRate > 0 ? purchaseRate : landedCost;
        row.ProfitPercentage =
          divisor > 0
            ? Number(
                (
                  ((currentSalesRate - previousSaleRate) / divisor) *
                  100
                ).toFixed(2),
              )
            : 0;

        const Discount = Number(row?.maxDiscount || maxDiscount || 0);
        const gd = Number(((row.SalesRate * Discount) / 100).toFixed(2));
        const tax = Number(
          (((row.SalesRate + gd) * Number(row?.GSTRate || 0)) / 100).toFixed(2),
        );
        row.Product_MRP = Number((row.SalesRate + gd + tax).toFixed(2));
        row.lossStatus = landedCost > row.SalesRate;
      } else {
        const p = Math.max(Number(row?.ProfitPercentage || 0), 3);
        row.ProfitPercentage = p;
        row.SalesRate = Number((landedCost * ((100 + p) / 100)).toFixed(2));

        const Discount = Number(row?.maxDiscount || maxDiscount || 0);
        const gd = Number(((row.SalesRate * Discount) / 100).toFixed(2));
        const tax = Number(
          (((row.SalesRate + gd) * Number(row?.GSTRate || 0)) / 100).toFixed(2),
        );
        row.Product_MRP = Number((row.SalesRate + gd + tax).toFixed(2));

        row.lossStatus = landedCost > row.SalesRate;
      }

      next[idx] = row;
      return next;
    });
  };

  const applyProfitThresholdAll = async (e) => {
    e.preventDefault();
    const thr = Number(profitThreshold);
    if (!Number.isFinite(thr) || thr < 0) return;
    setSaving(true);
    try {
      const universe = selectedIds.size
        ? rows.filter((r) => selectedIds.has(r?._id))
        : rows.slice();

      const toApply = universe
        .filter((r) => Number(r?.ProfitPercentage || 0) <= thr)
        .map((r) => ({ ...r }));

      toApply.forEach((row) => {
        const LandedCost =
          Number(row?.Purchase_Rate) > Number(row?.landedCost)
            ? Number(row?.Purchase_Rate)
            : Number(row?.landedCost);
        const p = Math.max(thr, 3);
        row.ProfitPercentage = p;
        row.SalesRate = Number((LandedCost * ((100 + p) / 100)).toFixed(2));
        const Discount = Number(row?.maxDiscount || maxDiscount || 0);
        const gd = Number(((row.SalesRate * Discount) / 100).toFixed(2));
        const tax = Number(
          (((row.SalesRate + gd) * Number(row?.GSTRate || 0)) / 100).toFixed(2),
        );
        row.Product_MRP = Number((row.SalesRate + gd + tax).toFixed(2));
      });

      const payload = toApply.map((ele) => ({
        id: ele?._id,
        Product_MRP: ele?.Product_MRP,
        SalesRate: ele?.SalesRate,
        ProfitPercentage: ele?.ProfitPercentage,
      }));

      if (payload.length) {
        await axiosConfig.put(Product_Price_Bulk_Update, { Products: payload });
      }

      if (selectedIds.size) {
        setFormValues((prev) =>
          prev.map((r) =>
            selectedIds.has(r?._id)
              ? { ...r, ...toApply.find((x) => x._id === r._id) }
              : r,
          ),
        );
      } else {
        setFormValues(
          rows.map((r) => {
            const x = withDerived(r, maxDiscount);
            x.__previousSalesRate = Number(r?.SalesRate ?? x.SalesRate ?? 0);
            return x;
          }),
        );
      }

      swal("Success", "Updated successfully", "success");
      await reload();
      setProfitThreshold("");
      setSelectedIds(new Set());
    } catch (err) {
      swal("Error", "Error occurred. Try again later.", "error");
    } finally {
      setSaving(false);
    }
  };

  const submitBulkEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = formValues.map((ele) => ({
        id: ele?._id,
        Product_MRP: ele?.Product_MRP,
        SalesRate: ele?.SalesRate,
        ProfitPercentage: ele?.ProfitPercentage,
      }));
      if (payload.length) {
        await axiosConfig.put(Product_Price_Bulk_Update, { Products: payload });
      }
      swal("Success", "Updated successfully", "success");
      await reload();
      closeModal();
    } catch (err) {
      swal("Error", "Error occurred. Try again later.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="pl-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Product Price List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 200px" }} />

              {isMaster && (
                <div style={{ minWidth: 260 }}>
                  <SuperAdminUI
                    onDropdownChange={(v) =>
                      localStorage.setItem(
                        "SuperadminIdByMaster",
                        JSON.stringify(v),
                      )
                    }
                    onSubmit={() => {
                      const saved = JSON.parse(
                        localStorage.getItem("SuperadminIdByMaster") || "null",
                      );
                      if (saved) {
                        const [id, db] = String(saved).split(" ");
                        reload();
                      }
                    }}
                  />
                </div>
              )}

              <div
                style={{
                  minWidth: 220,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.Edit && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={() => openModal(true)}
                >
                  Bulk Edit
                </Button>
              )}

              <Button
                outline
                color="primary"
                onClick={() => openModal(false)}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: "#e0f2fe",
                  flexWrap: "wrap",
                  maxWidth: 420,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                  }}
                >
                  Profit Colors
                </span>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={colorPillStyle}>
                    <input
                      type="color"
                      value={colorConfig.green}
                      onChange={(e) =>
                        updateColorConfig("green", e.target.value)
                      }
                      style={colorInputStyle}
                    />
                    <span style={colorLabelStyle}>≥ 15%</span>
                  </div>
                  <div style={colorPillStyle}>
                    <input
                      type="color"
                      value={colorConfig.orange}
                      onChange={(e) =>
                        updateColorConfig("orange", e.target.value)
                      }
                      style={colorInputStyle}
                    />
                    <span style={colorLabelStyle}>10 – 14.99%</span>
                  </div>
                  <div style={colorPillStyle}>
                    <input
                      type="color"
                      value={colorConfig.blue}
                      onChange={(e) =>
                        updateColorConfig("blue", e.target.value)
                      }
                      style={colorInputStyle}
                    />
                    <span style={colorLabelStyle}>5 – 9.99%</span>
                  </div>
                  <div style={colorPillStyle}>
                    <input
                      type="color"
                      value={colorConfig.red}
                      onChange={(e) => updateColorConfig("red", e.target.value)}
                      style={colorInputStyle}
                    />
                    <span style={colorLabelStyle}>0 – 4.99%</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  color="primary"
                  disabled={savingColors}
                  onClick={handleSaveColorConfig}
                  style={{ fontSize: 11, padding: "2px 10px" }}
                >
                  {savingColors ? "Saving..." : "Save"}
                </Button>
              </div>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={handleExportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() => handleExportXLSX("PriceList")}
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No products found.
              </div>
            ) : (
              <div className="pl-table-wrap">
                <table className="pl-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                      {permissions?.Delete && <th>ACTIONS</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r?._id || r?.id}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r)
                              : toStr(c.get(r))}
                          </td>
                        ))}
                        {permissions?.Delete && (
                          <td>
                            <div className="action-chips">
                              <span
                                className="chip chip-delete"
                                title="Delete"
                                onClick={() => handleDelete(r?._id)}
                              >
                                <Trash2 size={14} color="white" />
                              </span>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={modal}
        toggle={closeModal}
        scrollable={false}
        keyboard={false}
        size="xl"
        modalClassName="modal-xxl"
        contentClassName="no-scroll-modal"
      >
        <ModalHeader toggle={closeModal}>
          {bulkEdit ? "Edit Product Price List" : "Change Fields"}
        </ModalHeader>
        <ModalBody>
          {bulkEdit ? (
            <>
              <div className="pl-modal-head">
                <div style={{ fontWeight: 700 }}>
                  Edit Product MRP or Sales Rate
                </div>
              </div>

              <Row className="align-items-end mb-2">
                <Col lg="4" md="6" sm="12">
                  <Label className="mb-1">Search Product</Label>
                  <Input
                    placeholder="Type product name..."
                    onChange={(e) => {
                      const v = e.target.value.trim().toUpperCase();
                      if (!v)
                        return setFormValues(
                          rows.map((r) => {
                            const x = withDerived(r, maxDiscount);
                            x.__previousSalesRate = Number(
                              r?.SalesRate ?? x.SalesRate ?? 0,
                            );
                            return x;
                          }),
                        );
                      const filtered = rows.filter((r) =>
                        (r?.Product_Title || "").toUpperCase().includes(v),
                      );
                      setFormValues(
                        filtered.map((r) => {
                          const x = withDerived(r, maxDiscount);
                          x.__previousSalesRate = Number(
                            r?.SalesRate ?? x.SalesRate ?? 0,
                          );
                          return x;
                        }),
                      );
                    }}
                  />
                </Col>
                <Col lg="3" md="4" sm="12">
                  <Label className="mb-1">
                    Profit % (apply to items ≤ this %)
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    value={profitThreshold}
                    onChange={(e) => setProfitThreshold(e.target.value)}
                    placeholder="e.g. 3"
                  />
                </Col>
                <Col lg="2" md="2" sm="12">
                  <Button
                    color="primary"
                    disabled={!profitThreshold || saving}
                    onClick={applyProfitThresholdAll}
                    style={{ marginTop: 24 }}
                  >
                    {saving ? "Please wait..." : "Apply"}
                  </Button>
                </Col>
              </Row>

              <div className="pl-table-wrap" style={{ maxHeight: 480 }}>
                <Table
                  bordered
                  responsive
                  hover
                  className="align-middle"
                  style={{ margin: 0 }}
                >
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 52 }}>Select</th>
                      <th style={{ minWidth: 260 }}>Product Name</th>
                      <th style={{ minWidth: 90 }}>Landed Cost</th>
                      <th style={{ minWidth: 90 }}>Profit %</th>
                      <th style={{ minWidth: 110 }}>Sale Rate</th>
                      <th style={{ minWidth: 90 }}>Grade Dis.</th>
                      <th style={{ minWidth: 70 }}>Tax</th>
                      <th style={{ minWidth: 110 }}>MRP *</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formValues.map((row, idx) => {
                      const landed =
                        row?.Purchase_Rate > row?.landedCost
                          ? row?.Purchase_Rate
                          : row?.landedCost;
                      return (
                        <tr key={row?._id || idx}>
                          <td>
                            <Input
                              type="checkbox"
                              checked={selectedIds.has(row?._id)}
                              onChange={(e) => {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(row?._id);
                                  else next.delete(row?._id);
                                  return next;
                                });
                              }}
                            />
                          </td>
                          <td>
                            <span
                              style={{
                                color: getProfitColor(
                                  row?.ProfitPercentage,
                                  colorConfig,
                                ),
                                fontWeight: 600,
                              }}
                            >
                              {row?.Product_Title}
                            </span>
                          </td>
                          <td>
                            <Input readOnly value={landed || ""} />
                          </td>
                          <td>
                            <Input
                              type="number"
                              min={0}
                              value={row?.ProfitPercentage ?? 3}
                              onChange={(e) =>
                                handleInlineChange(
                                  idx,
                                  "ProfitPercentage",
                                  Number(e.target.value),
                                )
                              }
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              step="0.01"
                              value={
                                row?.SalesRate != null
                                  ? Number(row?.SalesRate).toFixed(2)
                                  : ""
                              }
                              onChange={(e) =>
                                handleInlineChange(
                                  idx,
                                  "SalesRate",
                                  Number(e.target.value),
                                )
                              }
                              style={{
                                color: row?.lossStatus ? "#b91c1c" : "#065f46",
                                fontWeight: 600,
                              }}
                            />
                          </td>
                          <td>
                            <Input readOnly value={row?.maxDiscount ?? 0} />
                          </td>
                          <td>
                            <Input readOnly value={row?.GSTRate ?? ""} />
                          </td>
                          <td>
                            <Input
                              required
                              value={row?.Product_MRP ?? ""}
                              onChange={(e) =>
                                handleInlineChange(
                                  idx,
                                  "Product_MRP",
                                  Number(e.target.value),
                                )
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-center mt-2">
                <Button
                  color="primary"
                  onClick={submitBulkEdit}
                  disabled={saving}
                >
                  {saving ? "Please wait..." : "Submit"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Row>
                <Col lg="4" md="4" sm="12" xl="4" xs="12">
                  <h5>Available Columns</h5>
                  <div className="listBox">
                    {baseColumns.map((col) => (
                      <div key={col.key} className="listItem">
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            margin: 0,
                          }}
                        >
                          <input
                            type="checkbox"
                            onChange={(e) =>
                              handleCheckbox(e.target.checked, col.key)
                            }
                          />
                          <span>{col.header}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      size="sm"
                      color="primary"
                      onClick={addSelectedColumns}
                    >
                      Add Selected →
                    </Button>
                  </div>
                </Col>

                <Col lg="8" md="8" sm="12" xl="8" xs="12">
                  <h5>Visible Columns</h5>
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                    }}
                  >
                    <div className="listBox" style={{ flex: 1 }}>
                      {selectedKeys.map((key, i) => {
                        const col = colMap.get(key);
                        if (!col) return null;
                        return (
                          <div
                            key={key}
                            className="listItem"
                            style={{
                              background:
                                i === selectedIndex ? "#1877f2" : "#fff",
                              color: i === selectedIndex ? "#fff" : "#000",
                            }}
                            onClick={() => setSelectedIndex(i)}
                          >
                            {col.header}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: "grid", gap: 8 }}>
                      <Button size="sm" onClick={shiftUp}>
                        ↑
                      </Button>
                      <Button size="sm" onClick={shiftDown}>
                        ↓
                      </Button>
                      <Button size="sm" color="danger" onClick={removeAtIndex}>
                        Remove
                      </Button>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, textAlign: "center" }}>
                    <Button color="primary" onClick={applyColumns}>
                      Submit
                    </Button>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};

const colorPillStyle = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 6px",
  borderRadius: 999,
  background: "#ffffff",
  border: "1px solid #bfdbfe",
};

const colorInputStyle = {
  width: 22,
  height: 22,
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
};

const colorLabelStyle = {
  fontSize: 10,
  fontWeight: 600,
};
